import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { KeyTerm, ImportantNote, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const biologyUnit7Notes: UnitNotes = {
  unitId: "unit-7",
  unitName: "Unit 7: Natural Selection",
  examWeight: "13–20%",
  focus: "Mechanisms of evolution, evidence for common ancestry, and the origin of life.",
  sections: [
    {
      id: "topic-7-1",
      title: "Topic 7.1: Introduction to Natural Selection",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧬 The Theory of Natural Selection</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Natural selection is the process by which organisms better adapted to their environment tend to survive and produce more offspring. <strong>Charles Darwin</strong> proposed that all life shares a common ancestor and species change via <strong>descent with modification</strong>.
            </p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <h4 className="font-bold text-slate-900 mb-2">The Mechanism of Selection:</h4>
              <ul className="space-y-3">
                <li className="text-sm text-slate-700"><strong>1. Overproduction:</strong> Populations produce more offspring than the environment can support.</li>
                <li className="text-sm text-slate-700"><strong>2. Competition:</strong> Limited resources (food, space, mates) lead to a "struggle for existence."</li>
                <li className="text-sm text-slate-700"><strong>3. Variation:</strong> Individuals possess phenotypic variations (genetic differences).</li>
                <li className="text-sm text-slate-700"><strong>4. Differential Survival/Reproduction:</strong> Adaptations increase the likelihood of passing on traits.</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏆 Evolutionary Fitness</h3>
            <KeyTerm 
              term="Fitness" 
              definition="Measured by reproductive success. It is the number of fertile offspring an organism leaves for the next generation relative to others in the population."
            />
            <ImportantNote>
              Fitness is not just physical strength. If an organism lives a long time but has zero offspring, its evolutionary fitness is <strong>zero</strong>.
            </ImportantNote>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 Biotic and Abiotic Factors</h3>
            <p className="text-sm text-slate-700 mb-4">The environment acts as the selective pressure. Environments fluctuate, and a trait beneficial in one may be detrimental in another.</p>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-green-50 p-3 rounded border border-green-200">
                <span className="font-bold text-green-900">Biotic Factors:</span> Predators, prey, vegetation.
              </div>
              <div className="bg-blue-50 p-3 rounded border border-blue-200">
                <span className="font-bold text-blue-900">Abiotic Factors:</span> Temperature, water, pH, sunlight.
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-2",
      title: "Topic 7.2: Natural Selection",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🖱️ Phenotypic Variation</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Natural selection acts on the <strong>phenotype</strong> (physical trait), not the genotype. However, allele frequencies change because phenotypes are genetically determined.
            </p>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h4 className="font-bold text-purple-900 mb-2">Sources of Variation:</h4>
              <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
                <li><strong>Mutation:</strong> Ultimate source of new alleles (random).</li>
                <li><strong>Sexual Reproduction:</strong> Crossing over, Independent Assortment, and Random Fertilization.</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📈 Types of Selection</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Directional Selection</h4>
                <p className="text-sm text-slate-700">Favors one extreme phenotype. <em>Example:</em> Antibiotic resistance in bacteria.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">Disruptive Selection</h4>
                <p className="text-sm text-slate-700">Favors both extremes, selects against the average. <em>Example:</em> Large or small beaks for large or small seeds.</p>
              </div>
              <div className="bg-gradient-to-r from-green-50 to-teal-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">Stabilizing Selection</h4>
                <p className="text-sm text-slate-700">Favors the intermediate/average; selects against extremes. <em>Example:</em> Human birth weights.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-3",
      title: "Topic 7.3: Artificial Selection",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🐕 Human-Driven Variation</h3>
            <KeyTerm 
              term="Artificial Selection" 
              definition="Humans manipulate variation by selecting and breeding individuals with desired traits (selective breeding)."
            />
            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 mt-4">
              <h4 className="font-bold text-amber-900">Convergent Evolution</h4>
              <p className="text-sm text-slate-700 mt-1">
                Similar selective pressures result in similar phenotypic adaptations in different populations. 
                This produces <strong>Analogous Structures</strong> (e.g., wings in birds and bats; hydrodynamic bodies in sharks and dolphins).
              </p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-4",
      title: "Topic 7.4: Population Genetics",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🎲 Random Occurrences (Non-Adaptive Evolution)</h3>
            <p className="text-slate-700 mb-4">Evolution is the change in genetic makeup (allele frequencies) of a population over time (<strong>microevolution</strong>).</p>
            <div className="space-y-4">
              <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-400">
                <h4 className="font-bold text-red-900">Genetic Drift</h4>
                <p className="text-sm text-slate-700">Random fluctuations in alleles, significant in <strong>small populations</strong>.</p>
                <ul className="list-disc pl-5 mt-2 text-xs text-slate-600 space-y-1">
                  <li><strong>Bottleneck Effect:</strong> Sudden reduction in size due to catastrophe. Genetic diversity is drastically reduced.</li>
                  <li><strong>Founder Effect:</strong> A few individuals isolate and establish a new population with a skewed gene pool.</li>
                </ul>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                <h4 className="font-bold text-blue-900">Gene Flow (Migration)</h4>
                <p className="text-sm text-slate-700">Movement of alleles into/out of a population. Tends to reduce differences between populations.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-5",
      title: "Topic 7.5: Hardy-Weinberg Equilibrium",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚖️ The H-W Model</h3>
            <p className="text-slate-700 mb-4">Describes a hypothetical, non-evolving population used as a <strong>Null Hypothesis</strong>.</p>
            <Mnemonic title="5 Conditions for Equilibrium" content="Large Population (No drift), No Migration (No flow), No Net Mutation, Random Mating, No Selection." />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧮 H-W Equations</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900 text-center mb-2">Allele Frequency</h4>
                <BlockMath math="p + q = 1" />
                <p className="text-xs text-slate-600 text-center mt-2">p = Dominant allele frequency<br/>q = Recessive allele frequency</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900 text-center mb-2">Genotype Frequency</h4>
                <BlockMath math="p^2 + 2pq + q^2 = 1" />
                <p className="text-xs text-slate-600 text-center mt-2">p² = Homozygous Dominant (AA)<br/>2pq = Heterozygous (Aa)<br/>q² = Homozygous Recessive (aa)</p>
              </div>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 mt-4">
              <h4 className="font-bold text-yellow-900">⭐ Solving Problems: The Golden Rule</h4>
              <p className="text-sm text-slate-700"><strong>Always find q first!</strong> Look for individuals with the recessive phenotype. Recessive count / Total = <InlineMath math="q^2" />. Take the square root to find <InlineMath math="q" />, then use <InlineMath math="1-q=p" />.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-6",
      title: "Topic 7.6: Evidence of Evolution",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🦴 Types of Evidence</h3>
            <div className="space-y-3">
              <div className="bg-white border rounded-lg p-4 shadow-sm">
                <h4 className="font-bold text-slate-900">1. Geographic/Geological</h4>
                <ul className="list-disc pl-5 mt-2 text-sm text-slate-700 space-y-1">
                  <li><strong>Fossils:</strong> Show progression of change. Dating via Carbon-14 or Radiometric methods.</li>
                  <li><strong>Biogeography:</strong> Species distribution (island species resemble nearby mainland).</li>
                </ul>
              </div>
              <div className="bg-white border rounded-lg p-4 shadow-sm">
                <h4 className="font-bold text-slate-900">2. Morphological</h4>
                <ul className="list-disc pl-5 mt-2 text-sm text-slate-700 space-y-1">
                  <li><strong>Homologous Structures:</strong> Similar anatomy from common ancestor (e.g., whale fins/human arms).</li>
                  <li><strong>Vestigial Structures:</strong> Remnants of ancestral features (e.g., pelvic bones in whales).</li>
                </ul>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 shadow-sm">
                <h4 className="font-bold text-blue-900">3. Biochemical/Molecular (Most Accurate)</h4>
                <p className="text-sm text-slate-700 mt-1">Comparing <strong>DNA nucleotide</strong> or <strong>amino acid sequences</strong>. All life uses the same genetic code (DNA/RNA) and 20 amino acids.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-7",
      title: "Topic 7.7: Common Ancestry",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚛️ Eukaryotic Conserved Core Processes</h3>
            <p className="text-slate-700 mb-4">Structural and functional evidence supporting relatedness of all eukaryotes:</p>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="bg-indigo-50 p-4 rounded border border-indigo-200 text-center">
                <h5 className="font-bold text-indigo-900 text-xs">Organelles</h5>
                <p className="text-[10px] text-slate-600 mt-1">Membrane-bound mitochondria/chloroplasts (Endosymbiosis).</p>
              </div>
              <div className="bg-indigo-50 p-4 rounded border border-indigo-200 text-center">
                <h5 className="font-bold text-indigo-900 text-xs">Chromosomes</h5>
                <p className="text-[10px] text-slate-600 mt-1">Linear chromosomes (vs. circular prokaryotic DNA).</p>
              </div>
              <div className="bg-indigo-50 p-4 rounded border border-indigo-200 text-center">
                <h5 className="font-bold text-indigo-900 text-xs">Introns</h5>
                <p className="text-[10px] text-slate-600 mt-1">Genes containing sequences that must be spliced out.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-8",
      title: "Topic 7.8: Continuing Evolution",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🕒 Ongoing Evolution</h3>
            <p className="text-slate-700 mb-4">Evolution is not a historical event; it happens constantly through genomic changes and is visible in the fossil record.</p>
            <div className="bg-rose-50 p-4 rounded-lg border border-rose-200">
              <h4 className="font-bold text-rose-900">Modern Examples:</h4>
              <ul className="list-disc pl-5 mt-2 text-sm text-slate-700 space-y-1">
                <li><strong>Antibiotic Resistance:</strong> Bacteria evolve rapidly due to fast reproduction.</li>
                <li><strong>Pesticide Resistance:</strong> Insects surviving agricultural chemicals.</li>
                <li><strong>Pathogens:</strong> SARS-CoV-2 and Flu viruses evolving new variants.</li>
              </ul>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-9",
      title: "Topic 7.9: Phylogeny",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌳 Visualizing Evolutionary History</h3>
            <div className="space-y-3">
              <KeyTerm term="Node" definition="Represents the most recent common ancestor (MRCA) of branching lineages." />
              <KeyTerm term="Clade" definition="A group consisting of an ancestor and all its descendants." />
              <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                <h4 className="font-bold text-emerald-900">Tree Construction Principles</h4>
                <ul className="list-disc pl-5 mt-2 text-xs text-slate-700 space-y-1">
                  <li><strong>Derived Characters:</strong> Traits that arise in a group and differ from the ancestor.</li>
                  <li><strong>Out-group:</strong> Taxon that diverged before the studied lineage; used for baseline traits.</li>
                  <li><strong>Parsimony:</strong> The simplest explanation (fewest changes) is usually correct.</li>
                </ul>
              </div>
              <KeyTerm term="Molecular Clocks" definition="Using constant mutation rates in genes to estimate the time since two species diverged." />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-10",
      title: "Topic 7.10: Speciation",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🛑 Reproductive Isolation</h3>
            <p className="text-sm text-slate-700 mb-4">A species is a group that can interbreed and produce <strong>viable, fertile offspring</strong>.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900 text-sm">Pre-zygotic (Prevents Fertilization)</h4>
                <p className="text-[10px] text-slate-600 mt-1">Habitat, Temporal, Behavioral, Mechanical, and Gametic Isolation.</p>
              </div>
              <div className="bg-pink-50 p-4 rounded-lg border border-pink-200">
                <h4 className="font-bold text-pink-900 text-sm">Post-zygotic (Prevents Fertile Adult)</h4>
                <p className="text-[10px] text-slate-600 mt-1">Reduced Hybrid Viability, Reduced Hybrid Fertility (Sterility), Hybrid Breakdown.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🗺️ Modes and Rates</h3>
            <div className="space-y-3">
              <div className="flex gap-4">
                <div className="flex-1 bg-slate-50 p-3 rounded border"><strong>Allopatric:</strong> Geographic barrier.</div>
                <div className="flex-1 bg-slate-50 p-3 rounded border"><strong>Sympatric:</strong> Same area (polyploidy, etc).</div>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900 text-sm">Speciation Rates</h4>
                <ul className="list-disc pl-5 mt-1 text-xs text-slate-700">
                  <li><strong>Punctuated Equilibrium:</strong> Rapid change after long stasis.</li>
                  <li><strong>Gradualism:</strong> Slow, constant change over long periods.</li>
                </ul>
              </div>
              <KeyTerm term="Adaptive Radiation" definition="Rapid evolution of many species from a single ancestor when new niches open up (e.g., Darwin's finches)." />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-11",
      title: "Topic 7.11: Variations in Populations",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📉 Diversity and Resilience</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              High genetic diversity makes populations resilient to environmental changes. Low diversity increases extinction risk (e.g., Cheetahs).
            </p>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <h4 className="font-bold text-red-900">Heterozygote Advantage: Sickle Cell</h4>
              <p className="text-sm text-slate-700 mt-2">
                <strong>AA:</strong> Susceptible to Malaria.<br/>
                <strong>aa:</strong> Sickle cell disease (fatal).<br/>
                <strong>Aa:</strong> Resistant to Malaria, no sickle cell disease. Selected for in malaria-prone regions.
              </p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-12",
      title: "Topic 7.12: Origins of Life on Earth",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌋 Primitive Earth and Abiotic Synthesis</h3>
            <p className="text-slate-700 mb-4">Earth formed ~4.6 bya. Early atmosphere: <InlineMath math="CO_2, CH_4, NH_3, H_2" />, but <strong>no free <InlineMath math="O_2" /></strong> (Reducing atmosphere).</p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <h4 className="font-bold text-slate-900">Miller-Urey Experiment</h4>
              <p className="text-sm text-slate-700 mt-1">Proved that organic molecules (amino acids) could form from inorganic precursors via lightning/UV energy.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧬 RNA World Hypothesis</h3>
            <p className="text-slate-700 mb-4">RNA was likely the first genetic material because:</p>
            <ul className="list-disc pl-6 text-sm text-slate-700 space-y-1">
              <li>It can store genetic information.</li>
              <li>It can act as an enzyme (<strong>Ribozymes</strong>).</li>
              <li>It is self-replicating.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💨 The Great Oxidation Event</h3>
            <div className="bg-cyan-50 p-4 rounded-lg border border-cyan-200">
              <p className="text-sm text-slate-700">Photosynthesis evolved in <strong>Cyanobacteria</strong> (~2.7 bya), releasing <InlineMath math="O_2" />. This killed anaerobes but allowed for aerobic respiration and eukaryotic life.</p>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default biologyUnit7Notes;